import React, { useState } from 'react';
import { X, Send, Bug, Lightbulb, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import { sendFeedback } from '../../services/feedbackService';
import toast from 'react-hot-toast';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug' | 'feature' | 'feedback';

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { user } = useAuth();
  const { selectedSchool } = useSchool();
  
  const [type, setType] = useState<FeedbackType>('feedback');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);

    try {
      // Get browser info for debugging
      const browserInfo = `${navigator.userAgent} | Screen: ${window.screen.width}x${window.screen.height}`;

      const result = await sendFeedback({
        type,
        subject,
        description,
        userEmail: user?.email || 'unknown@email.com',
        userName: user?.displayName || undefined,
        schoolName: selectedSchool?.name || undefined,
        browserInfo,
      });

      if (result.success) {
        toast.success('Thank you for your feedback! We\'ll review it shortly.');
        onClose();
        // Reset form
        setType('feedback');
        setSubject('');
        setDescription('');
      } else {
        toast.error('Failed to send feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      toast.error('An error occurred while sending feedback.');
    } finally {
      setSending(false);
    }
  };

  const typeOptions = [
    { value: 'bug' as FeedbackType, label: 'Bug Report', icon: Bug, color: 'text-red-500' },
    { value: 'feature' as FeedbackType, label: 'Feature Request', icon: Lightbulb, color: 'text-green-500' },
    { value: 'feedback' as FeedbackType, label: 'General Feedback', icon: MessageSquare, color: 'text-blue-500' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Send Feedback
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Feedback Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Feedback Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              {typeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setType(option.value)}
                    className={`
                      flex flex-col items-center p-3 rounded-lg border-2 transition-all
                      ${type === option.value 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 mb-1 ${type === option.value ? option.color : 'text-gray-400'}`} />
                    <span className={`text-xs ${type === option.value ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                type === 'bug' 
                  ? "Brief description of the issue"
                  : type === 'feature'
                  ? "What feature would you like to see?"
                  : "What's on your mind?"
              }
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              maxLength={100}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {subject.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === 'bug'
                  ? "Please describe the issue in detail. Include steps to reproduce if possible."
                  : type === 'feature'
                  ? "Please describe the feature and how it would help you."
                  : "Share your thoughts, suggestions, or compliments!"
              }
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white resize-none"
              maxLength={1000}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {description.length}/1000 characters
            </p>
          </div>

          {/* User Info Display */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm">
            <p className="text-gray-600 dark:text-gray-400 mb-1">
              <span className="font-medium">From:</span> {user?.email || 'Anonymous'}
            </p>
            {selectedSchool && (
              <p className="text-gray-600 dark:text-gray-400">
                <span className="font-medium">School:</span> {selectedSchool.name}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !subject.trim() || !description.trim()}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Feedback</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}